import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { SpinnerScreen } from './spinnerScreen';
import { getCustomString, getDecodedJwt } from '@bb-remark/web-utils';

export const CONVERSION_STATUS = {
  PENDING: 'pending',
  ERROR: 'error',
  EXPIRED: 'expired',
  COMPLETE: 'complete'
};

const POLLING_INTERVAL = 5000;
const POLLING_INTERVAL_SLOW = 30000;

const SLOW_TIME_LIMIT = 120000;

const TERMINAL_STATES = [
  CONVERSION_STATUS.ERROR,
  CONVERSION_STATUS.COMPLETE,
];

const checkConversionStatus = async (config) => {
  const { tenant_id, user_id, document_id } = getDecodedJwt();
  const { authPayload } = config;
  const endpoint = `/api/v1/pdfviewer/tenants/${tenant_id}/users/${user_id}/documents/${document_id}`;

  const response = await fetch(endpoint, {
    mode: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authPayload.jwt}`,
    },
  });

  if (!response.ok) {
    return { status: CONVERSION_STATUS.ERROR };
  }

  const body = await response.json();
  if (body.uploadStatus === CONVERSION_STATUS.COMPLETE) {
    return {
      status: CONVERSION_STATUS.COMPLETE,
      standalone: body.standalone
    };
  }

  return { status: CONVERSION_STATUS.PENDING };
};

const ConversionSpinner = ({ status }) => {
  let success = false;
  let error = false;

  let title = getCustomString('conversionSpinnerTitle');
  let description = getCustomString('conversionSpinnerDesc');
  switch (status) {
  case CONVERSION_STATUS.COMPLETE:
    success = true;
    break;
  case CONVERSION_STATUS.ERROR:
    title = getCustomString('conversionSpinnerTitleError');
    description = getCustomString('conversionSpinnerDescError');
    error = true;
    break;
  case CONVERSION_STATUS.EXPIRED:
    title = getCustomString('conversionSpinnerTitleExpired');
    description = getCustomString('conversionSpinnerDescExpired');
    break;
  default:
    error = false;
    success = false;
  }

  return (
    <SpinnerScreen title={title} description={description} success={success} error={error} testId={`conversion.${status}`}/>
  );
};

ConversionSpinner.propTypes = {
  status: PropTypes.string.isRequired,
};

export const ConversionScreen = ( props ) => {
  const {
    config,
    status,
    updateStatus
  } = props;

  const timeInPendingRef = useRef(0);

  useEffect(() => {
    if (TERMINAL_STATES.includes(status)) {
      return;
    }

    const delay = (status !== CONVERSION_STATUS.EXPIRED) ? POLLING_INTERVAL : POLLING_INTERVAL_SLOW;
    let intervalHandle = setInterval(async () => {
      timeInPendingRef.current += delay;

      //If we've polled for 2 minutes, provide 'Longer than usual' message
      if (timeInPendingRef.current > SLOW_TIME_LIMIT && status !== CONVERSION_STATUS.EXPIRED) {
        updateStatus(CONVERSION_STATUS.EXPIRED);
      }

      const conversionStatus = await checkConversionStatus(config);
      if (conversionStatus.status !== CONVERSION_STATUS.PENDING) {
        updateStatus(conversionStatus.status, conversionStatus.standalone);
      }
    }, delay);

    return () => clearInterval(intervalHandle);
  }, [status, updateStatus]);

  return <ConversionSpinner status={status} />;
};

ConversionScreen.propTypes = {
  config: PropTypes.object.isRequired,
  updateStatus: PropTypes.func.isRequired,
  status: PropTypes.string
};
